import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useGroupBlacklist, useToggleBlacklistEntry, mapRpcError, type BlacklistEntry } from "@/hooks/useGroupBlacklist";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";

const REASON_LABELS: Record<string, { label: string; tone: "default" | "secondary" | "outline" }> = {
  admin_directive: { label: "Pelo WhatsApp", tone: "default" },
  template_import: { label: "Template", tone: "secondary" },
  manual_admin_ui: { label: "Manual", tone: "outline" },
};

export function BlacklistTable({ groupId }: { groupId: string }) {
  const { data, isLoading, isError } = useGroupBlacklist(groupId);
  const toggle = useToggleBlacklistEntry(groupId);
  const { toast } = useToast();

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }

  if (isError) {
    return <p className="text-sm text-destructive">Erro ao carregar tópicos bloqueados.</p>;
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Nenhum tópico bloqueado neste grupo. Use o botão acima ou diga
          <span className="font-mono mx-1">"syra blacklist X"</span>
          no WhatsApp.
        </p>
      </div>
    );
  }

  const handleToggle = async (entry: BlacklistEntry, next: boolean) => {
    try {
      await toggle.mutateAsync({ entryId: entry.id, active: next });
      toast({
        title: next ? "Tópico reativado" : "Tópico desativado",
        description: `"${entry.topic_label}" agora está ${next ? "bloqueado" : "liberado"}.`,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Não foi possível alternar",
        description: mapRpcError(err),
      });
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tópico</TableHead>
          <TableHead>Origem</TableHead>
          <TableHead>Adicionado</TableHead>
          <TableHead className="text-right">Ativo</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((entry) => {
          const reason = REASON_LABELS[entry.reason ?? ""] ?? { label: entry.reason ?? "—", tone: "outline" as const };
          return (
            <TableRow key={entry.id} className={entry.active ? "" : "opacity-60"}>
              <TableCell className="font-medium">{entry.topic_label}</TableCell>
              <TableCell>
                <Badge variant={reason.tone}>{reason.label}</Badge>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: ptBR })}
              </TableCell>
              <TableCell className="text-right">
                <Switch
                  checked={entry.active}
                  onCheckedChange={(v) => handleToggle(entry, v)}
                  disabled={toggle.isPending}
                  aria-label={`Alternar ${entry.topic_label}`}
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
